// ** Modules
import React from 'react';
import { motion, type Transition } from 'framer-motion';

// ** UI
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

// ** Components
import StrapiImageRenderer from '@/components/strapi/StrapiImageRenderer';
import { ARTICLE_PAGE_HEADER_DEFAULTS_TYPED } from '@/components/common/defaults';

// ** Context
import { useHeaderOverride } from '@/context/HeaderOverrideContext';

// ** Types
import type { SxProps, Theme } from '@mui/material/styles';
import type { StrapiComponentProps } from '@/types/strapi';
import type { ComponentBlocksHero as BlocksHeroProps } from '@/types/graphql';

type InjectedHeroProps = {
	isMobile: boolean;
};
type ArticleVariantProps = StrapiComponentProps<BlocksHeroProps> & InjectedHeroProps;

// 720px is the article panel text content width — wider than the hero panel.
const CONTENT_MAX_WIDTH = { xs: '100%', lg: 720 } as const;

const ROOT_SX = {
	position: 'relative',
	display: { xs: 'block', lg: 'flex' },
	minHeight: { xs: 'auto', lg: 720 }
} as const;

const LEFT_IMAGE_SX = {
	position: 'relative',
	flex: { lg: '1 1 50%' },
	width: { xs: '100%', lg: '50%' },
	height: { xs: 420, sm: 520, lg: 720 },
	overflow: 'hidden'
} as const;

const RIGHT_PANEL_SX = {
	position: 'relative',
	flex: { lg: '1 1 50%' },
	width: { xs: '100%', lg: '50%' },
	minHeight: { xs: 520, lg: 720 }
} as const;

const RIGHT_PANEL_CONTENT_SX: SxProps<Theme> = (theme) => ({
	height: '100%',
	display: 'flex',
	alignItems: { xs: 'flex-start', lg: 'center' },
	pl: theme.layout.heroPanelPx,
	pr: {
		xs: theme.layout.heroPanelPx.xs,
		sm: theme.layout.heroPanelPx.sm,
		lg: `max(${theme.spacing(theme.layout.desktopGutter)}, calc((100vw - ${theme.layout.contentMaxWidthPx}px) / 2 + ${theme.spacing(theme.layout.desktopGutter)}))`
	},
	pt: { xs: 10, sm: 10, lg: 0 },
	pb: { xs: 10, lg: 0 }
});

const READING_TIME_BLOCK_SX = {
	mt: { xs: '98px', sm: 10, lg: '104px' },
	width: '100%',
	display: 'flex',
	justifyContent: { xs: 'center', sm: 'center', lg: 'flex-start' }
} as const;

const LINE_ANIMATE = {
	clipPath: ['inset(0% 0% 100% 0%)', 'inset(0% 0% 0% 0%)', 'inset(0% 0% 0% 0%)', 'inset(100% 0% 0% 0%)']
};

const LINE_TRANSITION: Transition = {
	duration: 2.2,
	times: [0, 0.35, 0.65, 1],
	ease: 'easeInOut',
	repeat: Infinity,
	repeatType: 'loop',
	repeatDelay: 0.3
};

const ArticleVariant = ({
	host,
	heroImage,
	heroColorVariant,
	eyebrow,
	headline,
	showMenu,
	subline = ARTICLE_PAGE_HEADER_DEFAULTS_TYPED.subline,
	copy = ARTICLE_PAGE_HEADER_DEFAULTS_TYPED.copy,
	readingTimeBold = ARTICLE_PAGE_HEADER_DEFAULTS_TYPED.readingTimeBold,
	readingTimeSuffix = ARTICLE_PAGE_HEADER_DEFAULTS_TYPED.readingTimeSuffix
}: ArticleVariantProps) => {
	const theme = useTheme();
	const themeMode = theme.palette.mode;
	const { setOverride } = useHeaderOverride();

	React.useEffect(() => {
		// 1. REGISTER: The moment this component mounts, it tells the header how to look
		setOverride({
			appBarVariant: heroColorVariant?.toLowerCase() === 'light' ? 'light' : 'dark',
			showMenu: !!showMenu
		});

		// 2. UNREGISTER: The moment this component leaves the DOM, it clears the override
		return () => setOverride(null);
	}, [heroColorVariant, showMenu, setOverride]);

	return (
		<Box sx={{ width: '100%' }}>
			<Box sx={ROOT_SX}>
				<Box sx={LEFT_IMAGE_SX}>
					<StrapiImageRenderer
						host={host}
						imageData={heroImage}
						themeMode={themeMode}
						alt={heroImage?.alternativeText || 'Hero Image'}
						fill
						loading="eager"
						sizes="(max-width: 1200px) 100vw, 50vw"
						style={{ objectFit: 'cover', objectPosition: 'center' }}
					/>
				</Box>

				<Box
					sx={{
						...RIGHT_PANEL_SX,
						backgroundColor:
							heroColorVariant?.toLocaleLowerCase() === 'light'
								? theme.palette.heroLightBackground.main
								: theme.palette.grey[600],
						color:
							heroColorVariant?.toLocaleLowerCase() === 'light'
								? theme.palette.common.black
								: theme.palette.common.white
					}}
				>
					<Box sx={RIGHT_PANEL_CONTENT_SX}>
						<Box sx={{ width: '100%' }}>
							<Stack spacing={0} sx={{ width: '100%', maxWidth: CONTENT_MAX_WIDTH }}>
								<Typography
									variant="label"
									sx={{
										color:
											heroColorVariant?.toLocaleLowerCase() === 'light'
												? theme.palette.common.black
												: theme.palette.common.white
									}}
								>
									{eyebrow}
								</Typography>

								<Box sx={{ mt: 4 }}>
									<Typography
										variant="display-md"
										sx={{
											fontWeight: 500,
											color:
												heroColorVariant?.toLocaleLowerCase() === 'light'
													? theme.palette.common.black
													: theme.palette.common.white
										}}
									>
										{headline}
									</Typography>
									<Typography
										variant="display-md"
										sx={{
											fontWeight: 300,
											color:
												heroColorVariant?.toLocaleLowerCase() === 'light'
													? theme.palette.common.black
													: theme.palette.common.white
										}}
									>
										{subline}
									</Typography>
								</Box>

								<Typography
									variant="body-md"
									sx={{
										mt: 8,
										color:
											heroColorVariant?.toLocaleLowerCase() === 'light'
												? theme.palette.common.black
												: theme.palette.common.white
									}}
								>
									{copy}
								</Typography>
							</Stack>

							<Box sx={READING_TIME_BLOCK_SX}>
								<Box
									sx={{
										display: 'flex',
										flexDirection: 'column',
										alignItems: 'center',
										width: '100%',
										maxWidth: CONTENT_MAX_WIDTH
									}}
								>
									<Box
										sx={{
											height: 32,
											display: 'flex',
											alignItems: 'flex-start'
										}}
									>
										<motion.div
											animate={LINE_ANIMATE}
											transition={LINE_TRANSITION}
											style={{
												width: 0,
												height: 32,
												borderLeft: `2px solid ${
													theme.palette.primary.lighter ?? theme.palette.primary.light ?? theme.palette.primary.main
												}`
											}}
										/>
									</Box>

									<Typography
										variant="label"
										sx={{
											mt: 4,
											color:
												heroColorVariant?.toLocaleLowerCase() === 'light'
													? theme.palette.common.black
													: theme.palette.common.white,
											textAlign: 'center'
										}}
									>
										<Box component="span" sx={{ fontWeight: 700 }}>
											{readingTimeBold}
										</Box>{' '}
										<Box component="span" sx={{ fontWeight: 400 }}>
											{readingTimeSuffix}
										</Box>
									</Typography>
								</Box>
							</Box>
						</Box>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

export default ArticleVariant;
